/**
 * Admin Audit Viewer - P1 Feature
 * 
 * A searchable, filterable "time machine" for all admin actions.
 * Supports filtering by:
 * - Free text (email, user_id, report_id, flag, note)
 * - Action type
 * - Actor (admin who performed action)
 * - Target user_id
 * - Time window
 */

import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../App";
import { ArrowLeft, Search, RefreshCw, User, Clock, ExternalLink } from "lucide-react";

const API = window.location.origin;

function fmt(v) {
  try {
    return v ? new Date(v).toLocaleString() : "—";
  } catch {
    return "—";
  }
}

function Pill({ children, color = "slate" }) {
  const colorClasses = {
    slate: "border-slate-700 bg-slate-800/50 text-slate-300",
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    rose: "border-rose-500/30 bg-rose-500/10 text-rose-400",
    blue: "border-blue-500/30 bg-blue-500/10 text-blue-400",
    purple: "border-purple-500/30 bg-purple-500/10 text-purple-400",
  };
  return (
    <span className={`text-xs px-2 py-1 rounded-full border ${colorClasses[color] || colorClasses.slate}`}>
      {children}
    </span>
  );
}

export default function AdminAuditPage() {
  const { token } = useContext(AuthContext);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  // Filter state - initialize from URL params if present
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [type, setType] = useState(searchParams.get("type") || "");
  const [actor, setActor] = useState(searchParams.get("actor") || "");
  const [userId, setUserId] = useState(searchParams.get("user_id") || "");
  const [days, setDays] = useState(searchParams.get("days") || "7");

  async function loadTypes() {
    try {
      const res = await fetch(`${API}/api/admin/audit/types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) setTypes(data.types || []);
    } catch (err) {
      console.error("[AdminAudit] Failed to load types:", err);
    }
  }

  function computeRange() {
    if (!days) return { start: "", end: "" };
    const d = parseInt(days, 10);
    if (!d) return { start: "", end: "" };
    const end = new Date();
    const start = new Date(Date.now() - d * 24 * 60 * 60 * 1000);
    return { start: start.toISOString(), end: end.toISOString() };
  }

  async function load() {
    setBusy(true);
    setToast(null);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (type) params.set("type", type);
      if (actor) params.set("actor", actor);
      if (userId) params.set("user_id", userId);

      const range = computeRange();
      if (range.start) params.set("start", range.start);
      if (range.end) params.set("end", range.end);

      params.set("limit", "50");

      const res = await fetch(`${API}/api/admin/audit?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed to load audit");
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadTypes();
    load();
    
  }, []);

  // Get color for action type
  function getTypeColor(t) {
    if (!t) return "slate";
    if (t.includes("ban") || t.includes("suspend")) return "rose";
    if (t.includes("flag")) return "amber";
    if (t.includes("report") || t.includes("resolution")) return "purple";
    if (t.includes("dispute")) return "amber";
    if (t.includes("clear") || t.includes("enable")) return "emerald";
    return "blue";
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <button
              onClick={() => nav("/admin")}
              className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Admin</span>
            </button>
            <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
              Admin Audit Log
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Who did what, when. Filter it like a detective.
            </p>
          </div>

          <button
            onClick={load}
            disabled={busy}
            className="flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Loading…" : "Refresh"}
          </button>
        </div>

        {/* Filters */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                className="w-full rounded-xl border border-slate-700 bg-slate-800/50 pl-10 pr-4 py-2 text-sm placeholder:text-slate-500 focus:border-purple-500 focus:outline-none transition"
                placeholder="Search (email, user_id, report_id, flag…)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
              />
            </div>

            <select
              className="rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none transition"
              value={type}
              onChange={(e) => setType(e.target.value)}
              aria-label="Filter by type"
            >
              <option value="">All types</option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>

            <input
              className="rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm w-48 placeholder:text-slate-500 focus:border-purple-500 focus:outline-none transition"
              placeholder="Actor (email or id)"
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />

            <input
              className="rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm w-56 placeholder:text-slate-500 focus:border-purple-500 focus:outline-none transition"
              placeholder="Target user_id"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />

            <select
              className="rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none transition"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              aria-label="Time window"
            >
              <option value="">All time</option>
              <option value="1">Last 24h</option>
              <option value="7">Last 7d</option>
              <option value="30">Last 30d</option>
              <option value="90">Last 90d</option>
            </select>

            <button
              onClick={load}
              disabled={busy}
              className="rounded-xl border border-purple-500/30 bg-purple-500/10 px-4 py-2 text-sm font-medium text-purple-400 hover:bg-purple-500/20 disabled:opacity-50 transition"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400 px-4 py-3 text-sm">
            {toast.msg}
          </div>
        )}

        {/* Results count */}
        <div className="text-sm text-slate-400 mb-4">
          Showing {items.length} of {total} audit events
        </div>

        {/* Audit Items */}
        <div className="space-y-3">
          {items.map((a, idx) => (
            <div
              key={idx}
              className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 hover:border-slate-700 transition"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill color={getTypeColor(a.type)}>{a.type || "unknown"}</Pill>
                  {a.flag && <Pill color="amber">flag={a.flag}</Pill>}
                  {typeof a.enabled === "boolean" && (
                    <Pill color={a.enabled ? "emerald" : "slate"}>
                      enabled={String(a.enabled)}
                    </Pill>
                  )}
                  {a.report_id && <Pill color="purple">report={a.report_id}</Pill>}
                  {a.dispute_id && <Pill color="amber">dispute={a.dispute_id}</Pill>}
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Clock className="w-3 h-3" />
                  {fmt(a.created_at)}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Actor */}
                <div>
                  <div className="text-xs text-slate-500 mb-1">Actor (Admin)</div>
                  <div className="text-sm text-slate-300">
                    {a.actor_email || a.actor_id || "—"}
                  </div>
                </div>

                {/* Target User */}
                <div>
                  <div className="text-xs text-slate-500 mb-1">Target User</div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm text-slate-300 font-mono">
                      {a.user_id || "—"}
                    </div>
                    {a.user_id && (
                      <button
                        className="flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1 text-xs text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition"
                        onClick={() => nav(`/admin/users/${a.user_id}`)}
                      >
                        <User className="w-3 h-3" />
                        User 360
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Note */}
              {a.note && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  <div className="text-xs text-slate-500 mb-1">Note</div>
                  <div className="text-sm text-slate-300 whitespace-pre-wrap">
                    {a.note}
                  </div>
                </div>
              )}

              {/* Meta (if present and has content) */}
              {a.meta && Object.keys(a.meta).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  <div className="text-xs text-slate-500 mb-1">Additional Details</div>
                  <div className="text-xs text-slate-400 font-mono bg-slate-800/50 rounded-lg p-2 overflow-x-auto">
                    {JSON.stringify(a.meta, null, 2)}
                  </div>
                </div>
              )}
            </div>
          ))}

          {items.length === 0 && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-8 text-center">
              <div className="text-slate-400 mb-2">No audit events found</div>
              <div className="text-xs text-slate-500">
                Try adjusting your filters or time window
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
